import moment from "moment";
import { IParlayOutcome, Leagues, Operators, OptimizerData, Payout, PickemParlayType, Timeframe } from "./store/types";
import reduce from 'lodash/reduce';

export const getOperatorPayout = (pickCount:number,optimizerData:OptimizerData) => {
    let defaultPayout = {pickSetSize:0, lowPayoutMultiplier:0,averagePayoutMultiplier:0,numberOfPicksCorrect:0,highPayoutMultiplier:0} as Payout;
    if (optimizerData) {
       return optimizerData.payouts?.filter(x=>x.numberOfPicksCorrect === pickCount)[0] 
       ?? defaultPayout;
    }
    return defaultPayout;
  };

  export const getLegCountAndPickemTypeToPayoutsDictionary = (
   operator: string,
   pickemParlayType: string,
   outcomes: { oddsDecimal: number }[]
 ): { [key: number]: number } => {
   return legCountAndPickemTypeToPayoutsDictionary[`${operator}|${pickemParlayType}|${outcomes.length}`];
 };

  export const getPickemCalculatedData = (outcomes: IParlayOutcome[], operator: Operators, pickemParlayType: PickemParlayType) => {
   if (!outcomes.length) {
     return {
       oddsAmerican: '',
       expectedWinPct: Infinity,
       expectedRoiPct: Infinity,
     };
   }
   const payouts = getLegCountAndPickemTypeToPayoutsDictionary(operator, pickemParlayType, outcomes);
   if (payouts === undefined) {
     return {
       oddsAmerican: '',
       expectedWinPct: Infinity,
       expectedRoiPct: Infinity,
     };
   }
   // True Win % = product of individual leg win probabilities
   const expectedWinPct = Number(
     reduce(outcomes, (a, c) => a * Number(c.expectedWin), 1),
   ) * 100;

   // EV = product of individual leg EVs (simple multiplication)
   // Check if any leg is missing expectedRoi first
   const hasAllRoi = outcomes.every((outcome) => outcome.expectedRoi != null);
   if (!hasAllRoi) {
     return {
       oddsAmerican: Object.keys(payouts).length >= 1 ? `+${(Object.values(payouts)[Object.values(payouts).length - 1] - 1) * 100}` : '',
       expectedWinPct,
       expectedRoiPct: Infinity,
     };
   }

   const expectedRoiPct = Number(
     reduce(outcomes, (a, c) => a * Number(c.expectedRoi), 1),
   ) * 100;
   return {
     oddsAmerican: Object.keys(payouts).length >= 1 ? `+${(Object.values(payouts)[Object.values(payouts).length - 1] - 1) * 100}` : '',
     expectedWinPct,
     expectedRoiPct,
   };
 };

 // This dictionary retrieves the payouts for different pickem parlays based on the operator, pickem parlay type, and number of legs
export const legCountAndPickemTypeToPayoutsDictionary: {
   [string: string]: { [number: number]: number };
 } = {
   'PrizePicks|Flex|3': {
     3: 2.25,
     2: 1.25,
   },
   'PrizePicks|Flex|4': {
     4: 5,
     3: 1.5,
   },
   'PrizePicks|Flex|5': {
     5: 10,
     4: 2,
     3: 0.4,
   },
   'PrizePicks|Flex|6': {
     6: 25,
     5: 2,
     4: 0.4,
   },
   'PrizePicks|Power|2': {
     2: 3,
   },
   'PrizePicks|Power|3': {
     3: 5,
   },
   'PrizePicks|Power|4': {
     4: 10,
   },
   'NoHouseAdvantage|Power|2': {
     2: 3.2,
   },
   'NoHouseAdvantage|Power|3': {
     3: 6,
   },
   'NoHouseAdvantage|Power|4': {
     4: 11,
   },
   'NoHouseAdvantage|Power|5': {
     5: 21,
   },
   'NoHouseAdvantage|Flex|4': {
     4: 6,
     3: 1.6,
   },
   'NoHouseAdvantage|Flex|5': {
     5: 12,
     4: 2.6,
   },
   'Underdog|Power|2': {
     2: 3,
   },
   'Underdog|Power|3': {
     3: 6,
   },
   'Underdog|Power|4': {
     4: 10,
   },
   'Underdog|Power|5': {
     5: 20,
   },
   'Underdog|Flex|3': {
     3: 3,
     2: 1,
   },
   'Underdog|Flex|4': {
     4: 6,
     3: 1.5,
   },
   'Underdog|Flex|5': {
     5: 10,
     4: 2.5,
   },
   'Betr|Power|2': {
     2: 3,
   },
   'Betr|Power|3': {
     3: 5,
   },
   'Betr|Power|4': {
     4: 10,
   },
   'Betr|Power|5': {
     5: 20,
   },
   'Betr|Power|6': {
     6: 30,
   },
   'Betr|Power|7': {
     7: 50,
   },
   'Betr|Power|8': {
     8: 100,
   },
   'Dabble|Power|2': {
     2: 3,
   },
   'Dabble|Power|3': {
     3: 6,
   },
   'Dabble|Power|4': {
     4: 10,
   },
   'Dabble|Power|5': {
     5: 20,
   },
   'Dabble|Power|6': {
     6: 35,
   },
   'Dabble|Power|7': {
     7: 60,
   },
   'Dabble|Power|8': {
     8: 100,
   },
   'Dabble|Flex|3': {
     2: 1.25,
     3: 2.5,
   },
   'Dabble|Flex|4': {
     3: 1.5,
     4: 5,
   },
   'Dabble|Flex|5': {
     3: 0.4,
     4: 1.5,
     5: 10,
   },
   'Dabble|Flex|6': {
     4: 0.4,
     5: 1.5,
     6: 25,
   },
   'Dabble|Flex|7': {
     5: 1,
     6: 2.5,
     7: 35,
   },
   'Dabble|Flex|8': {
     6: 1.5,
     7:5,
     8: 50,
   },
   'Pick6|Flex|2': {
     2: 2.4,
   },
   'Pick6|Flex|3': {
     3: 4.24,
   },
   'Pick6|Flex|4': {
     4: 8.34,
   },
   'Pick6|Flex|5': {
     4: 1.2,
     5: 10,
   },
   'Pick6|Flex|6': {
     5: 1.83,
     6: 19,
   },
 };

 export function evAgeLabel(evAgeDate: Date| null): string {
  if (evAgeDate == null) {
    return '-';
  }

  const dateCompare = (Date.now() - new Date(`${evAgeDate}Z`).getTime()) / 1000;
  if (dateCompare < 60) {
    return '< 1 Min';
  }
  if (dateCompare < 3600 && dateCompare > 60) {
    return `${Math.round(dateCompare / 60)} Mins`;
  }
  if (dateCompare > 3600 && dateCompare < 86400) {
    return `${Math.round(dateCompare / 60 / 60)} Hrs`;
  }
  if (dateCompare > 86400) {
    return `${Math.round(dateCompare / 60 / 60 / 24)} Days`;
  }
  return '-';
}

export const getLeague = (league?: string): Leagues => {
  switch (league?.toUpperCase()) {
    case 'NBA':
      return Leagues.NBA;
      case 'WNBA':
        return Leagues.WNBA;
    case 'NFL':
      return Leagues.NFL;
    case 'MLB':
      return Leagues.MLB;
    case 'NHL':
      return Leagues.NHL;
    case 'GOLF':
      return Leagues.GOLF;
    case 'MMA':
      return Leagues.MMA;
    case 'UFC':
      return Leagues.UFC;
    case 'BL':
    case 'BUNDESLIGA':
      return Leagues.BL;
    case 'EPL':
      return Leagues.EPL;
    case 'TENNIS':
      return Leagues.TENNIS;
    case 'CL':
      return Leagues.CL;
    case 'NCAAB':
      return Leagues.NCAAB;
    case 'NCAAF':
      return Leagues.NCAAF;
    case 'ALL':
      return Leagues.ALL;
    default:
      return Leagues.ALL;
  }
};

export const getISOStringsForTimeframe = (timeframe: Timeframe): {startISOString: string | undefined, endISOString: string | undefined} | undefined => {
  switch(timeframe) {
    case Timeframe.TODAY:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.TOMORROW:
      return {
        startISOString: moment().add(1, 'day').startOf('day').toISOString(),
        endISOString:moment().add(1, 'day').endOf('day').toISOString()
      };
    case Timeframe.YESTERDAY:
    return {
        startISOString: moment().subtract(1, 'day').startOf('day').toISOString(),
        endISOString:moment().subtract(1, 'day').endOf('day').toISOString()
      };
    case Timeframe.WEEK:
      return {
        startISOString: moment().startOf('week').toISOString(),
        endISOString: moment().endOf('week').toISOString()
      };
    case Timeframe.MONTH:
      return {
        startISOString: moment().startOf('month').toISOString(),
        endISOString: moment().endOf('month').toISOString()
      };
    case Timeframe.YEAR:
      return {
        startISOString: moment().startOf('year').toISOString(),
        endISOString: moment().endOf('year').toISOString()
      };
    case Timeframe.ALL:
      return undefined;
    case Timeframe.PAST_WEEK:
      return {
        startISOString: moment().startOf('week').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.PAST_MONTH:
      return {
        startISOString: moment().startOf('month').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.PAST_YEAR:
      return {
        startISOString: moment().startOf('year').toISOString(),
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.PAST_ALL:
      return {
        startISOString: undefined,
        endISOString: moment().endOf('day').toISOString()
      };
    case Timeframe.FUTURE_WEEK:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('week').toISOString()
      };
    case Timeframe.FUTURE_MONTH:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('month').toISOString()
      };
    case Timeframe.FUTURE_YEAR:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().endOf('year').toISOString()
      };
    case Timeframe.FUTURE_ALL:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: undefined
      };    
    case Timeframe.PAST_7_DAYS:
      return {
        startISOString: moment().subtract(7, 'days').startOf('day').toISOString(),
        endISOString: moment().startOf('day').toISOString()
        };
    case Timeframe.PAST_30_DAYS:
      return {
        startISOString: moment().subtract(30, 'days').startOf('day').toISOString(),
        endISOString: moment().startOf('day').toISOString()
        };
    case Timeframe.PAST_365_DAYS:
      return {
        startISOString: moment().subtract(365, 'days').startOf('day').toISOString(),
        endISOString: moment().startOf('day').toISOString()
        };
    case Timeframe.FUTURE_7_DAYS:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().add(7, 'days').endOf('day').toISOString()
      };
    case Timeframe.FUTURE_30_DAYS:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().add(30,'days').startOf('day').toISOString(),
      };
    case Timeframe.FUTURE_365_DAYS:
      return {
        startISOString: moment().startOf('day').toISOString(),
        endISOString: moment().add(365,'days').startOf('day').toISOString(),
      };
    case Timeframe.SEASON_LONG: 
    //Intentional fall through. Returning no dates for this case was the existing logic.
    case Timeframe.PREVIOUS_SEASON: 
    //Intentional fall through. Returning no dates for this case was the existing logic.
    default:
      return undefined;
  };
};